import React, { memo, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { ItemCard } from '../../../ui/ItemCard';
import { useBookmarks } from '../../../../hooks/useBookmarks';
import { ItemList } from '../../../ui/ItemCard/styled';
import { residenceItemsQuery } from '../../../../api/queries';

const ItemsSection = memo(
  ({ residenceId, itemsRooms, name, itemsCount, residenceItems = [] }) => {
    const { t } = useTranslation();
    const [showMore, setShowMore] = useState(true);
    const [currentRooms, setCurrentRooms] = useState('all');
    const [selectState, setSelectState] = useState('BUMPED_AT_DESC');
    const { isBookmarked } = useBookmarks();

    const hasPrefetchedItems =
      Array.isArray(residenceItems) && residenceItems.length > 0;
    const { data, fetchMore } = useQuery(residenceItemsQuery, {
      variables: {
        limit: 6,
        sort: selectState,
        filter: {
          residenceId,
          rooms: currentRooms === 'all' ? '' : String(currentRooms)
        }
      },
      skip: hasPrefetchedItems
    });

    const items =
      data?.items ?? (Array.isArray(residenceItems) ? residenceItems : []);

    useEffect(() => {
      if (items.length === itemsCount) {
        setShowMore(false);
      } else if (items.length < 6) {
        setShowMore(false);
      } else {
        setShowMore(true);
      }
    }, [items, itemsCount]);

    const filterValues = itemsRooms && ['all', ...itemsRooms];

    const filterValuesTranslations = itemsRooms && [
      t('common.all'),
      ...itemsRooms.map(room => room)
    ];

    const buttonList = () =>
      itemsRooms &&
      filterValuesTranslations?.map((item, i) => (
        <button
          key={i}
          data-stat="residence-items-filter"
          className={cn('ap-filter__header-btn', {
            active: filterValues[i] === currentRooms
          })}
          onClick={() => {
            setCurrentRooms(filterValues[i]);
          }}
        >
          {item}
        </button>
      ));

    const sortTypes = [
      {
        id: '1',
        name: t('search.sort.sort_types.date'),
        params: 'BUMPED_AT_DESC'
      },
      { id: '2', name: t('search.sort.sort_types.square'), params: 'AREA_ASC' },
      {
        id: '3',
        name: t('search.sort.sort_types.price_high'),
        params: 'PRICE_ASC'
      },
      {
        id: '4',
        name: t('search.sort.sort_types.price_low'),
        params: 'PRICE_DESC'
      }
    ];
    const sortOptions = sortTypes.map(item => (
      <option key={item.id} value={item.params}>
        {item.name}
      </option>
    ));

    return (
      <div className="residence-items__container" id="residence-items">
        <div className="residence-items__title">
          {t('residences.items_title', { residence_name: name })}
        </div>
        <div className="residence-items__description">
          {t('residences.items_count', {
            count: currentRooms === 'all' ? itemsCount : items.length
          })}
          <div className="residence-items__search">
            <select // eslint-disable-line jsx-a11y/no-onchange
              name="residence-items__search"
              value={selectState}
              onChange={e => setSelectState(e.target.value)}
            >
              {sortOptions}
            </select>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="7"
              viewBox="0 0 10 7"
            >
              <path
                fill="#4264FD"
                stroke="#4264FD"
                d="M1.601 1.11a.339.339 0 0 0-.498 0 .388.388 0 0 0 0 .526L5.126 5.89c.138.145.36.145.498 0l4.023-4.255a.388.388 0 0 0 0-.527.339.339 0 0 0-.498 0l-3.774 3.88-3.774-3.88z"
              />
            </svg>
          </div>
        </div>
        {itemsRooms && (
          <div className="residence-items__row">{buttonList()}</div>
        )}
        <ItemList className="residence-items">
          {items?.map(item => (
            <ItemCard
              stat="residence-item"
              key={item.id}
              {...item}
              inBookmarks={isBookmarked(item.id)}
            />
          ))}
        </ItemList>

        {showMore && (
          <button
            className="residence-items__show-more"
            onClick={() => {
              if (!fetchMore) return;
              fetchMore({
                variables: {
                  limit: 24,
                  offset: items.length
                }
              });
            }}
          >
            {t('residences.show_more')}
          </button>
        )}
      </div>
    );
  }
);

ItemsSection.displayName = `ItemsSection`;

ItemsSection.propTypes = {
  residenceId: PropTypes.string,
  itemsRooms: PropTypes.array,
  name: PropTypes.string,
  itemsCount: PropTypes.number,
  residenceItems: PropTypes.array
};

export default ItemsSection;
